import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CookService from '../../services/cook.service';
import TimeChart, { INK, SERIES_COLORS } from './TimeChart.component';
import PlannedMealLink from './PlannedMealLink.component';
import CookNotes from './CookNotes.component';
import { fmtDuration, fmtTemp, fmtWeight, fmtFoodType, STATUS_LABELS } from './cookFormat';

const useStyles = makeStyles((theme) => ({
  header: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap', margin: '8px 0' },
  facts: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1, 3), margin: theme.spacing(1, 0, 2) },
  fact: { minWidth: 90 },
  factLabel: { color: '#888', fontSize: 12 },
  panel: { padding: theme.spacing(1.5, 2), marginBottom: theme.spacing(2) },
  panelHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: theme.spacing(1), marginBottom: theme.spacing(1) },
  note: { color: '#888', fontSize: 12, marginTop: theme.spacing(1) },
  modelSelect: { minWidth: 120 }
}));

const THERMISTORS = [1, 2, 3, 4, 5, 6, 7, 8];
const ROLES = [
  { key: 'coreC', index: 'coreIndex', label: 'Core', color: SERIES_COLORS[0] },
  { key: 'surfaceC', index: 'surfaceIndex', label: 'Surface', color: SERIES_COLORS[1] },
  { key: 'ambientC', index: 'ambientIndex', label: 'Ambient', color: SERIES_COLORS[2] }
];

const fmtC = v => `${Math.round(v)}°`;
const fmtElapsed = s => fmtDuration(s);

// Contiguous runs of estimates the model classified as a stall, as [from, to] seconds.
function stallRanges(estimates) {
  const ranges = [];
  let from = null;
  let prev = null;
  estimates.forEach(e => {
    const stalled = (e.regime || '').toLowerCase() === 'stall';
    if (stalled && from === null) from = e.atS;
    if (!stalled && from !== null) { ranges.push([from, prev]); from = null; }
    prev = e.atS;
  });
  if (from !== null) ranges.push([from, prev]);
  return ranges;
}

const Fact = ({ label, children }) => {
  const classes = useStyles();
  return (
    <div className={classes.fact}>
      <div className={classes.factLabel}>{label}</div>
      <div>{children}</div>
    </div>
  );
};

const CookDetailPage = () => {
  const classes = useStyles();
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [modelVersion, setModelVersion] = useState('');
  const [message, setMessage] = useState(null); // {severity,text}

  const apiErr = err => err.response?.data?.message || 'Request failed.';

  const load = () => CookService.get(id)
    .then(res => setDetail(res.data))
    .catch(err => setMessage({ severity: 'error', text: apiErr(err) }));

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const versions = useMemo(() => (detail
    ? [...new Set(detail.estimates.map(e => e.modelVersion))].sort()
    : []), [detail]);

  useEffect(() => {
    if (versions.length && !versions.includes(modelVersion)) setModelVersion(versions[versions.length - 1]);
  }, [versions, modelVersion]);

  const charts = useMemo(() => {
    if (!detail) return null;
    const { cook, samples, estimates, markers } = detail;
    const lastSample = samples.length ? samples[samples.length - 1].elapsedS : 0;
    const lastEstimate = estimates.length ? estimates[estimates.length - 1].atS : 0;
    const xMax = Math.max(lastSample, lastEstimate, markers.removedS || 0, 1);

    const tempSeries = [
      ...THERMISTORS.map(i => ({
        key: `t${i}`,
        label: 'Thermistors t1–t8',
        color: INK.muted,
        opacity: 0.35,
        strokeWidth: 1,
        inTooltip: false,
        inLegend: i === 1,
        points: samples.map(s => [s.elapsedS, s[`t${i}C`]])
      })),
      ...ROLES.map(r => ({
        key: r.key,
        label: r.label,
        color: r.color,
        endLabel: true,
        points: samples.map(s => [s.elapsedS, s[r.key]])
      }))
    ];

    const vLines = [];
    if (markers.wrappedS !== null) vLines.push({ key: 'wrap', x: markers.wrappedS, label: 'Wrapped' });
    if (markers.removedS !== null) vLines.push({ key: 'removed', x: markers.removedS, label: 'Removed' });

    const selected = estimates.filter(e => e.modelVersion === modelVersion);
    const xRanges = stallRanges(selected).map(([from, to], i) => ({
      key: `stall${i}`, from, to, label: i === 0 ? 'Stall' : null
    }));

    const finishSeries = versions.map((v, i) => ({
      key: v,
      label: `Predicted (${v})`,
      color: SERIES_COLORS[i % SERIES_COLORS.length],
      endLabel: true,
      points: estimates.filter(e => e.modelVersion === v && e.etaS !== null).map(e => [e.atS, e.atS + e.etaS])
    }));
    const finishBands = versions.map((v, i) => ({
      key: `band-${v}`,
      color: SERIES_COLORS[i % SERIES_COLORS.length],
      points: estimates.filter(e => e.modelVersion === v && e.etaLowS !== null && e.etaHighS !== null)
        .map(e => [e.atS, e.atS + e.etaLowS, e.atS + e.etaHighS])
    }));
    const finishLines = markers.reachedTargetS !== null
      ? [{ key: 'actual', y: markers.reachedTargetS, label: `Actual ${fmtElapsed(markers.reachedTargetS)}` }]
      : [];

    // Raw thermistors + which one plays each role, for the temperature tooltip.
    const sampleAt = x => {
      let best = samples[0];
      samples.forEach(s => { if (Math.abs(s.elapsedS - x) < Math.abs(best.elapsedS - x)) best = s; });
      return best;
    };
    const tempTooltipExtra = x => {
      if (!samples.length) return null;
      const s = sampleAt(x);
      return (
        <div style={{ marginTop: 4, color: INK.muted }}>
          <div>{THERMISTORS.map(i => `t${i} ${s[`t${i}C`].toFixed(1)}`).join(' · ')}</div>
          <div>{ROLES.map(r => `${r.label} = t${s[r.index]}`).join(' · ')}</div>
        </div>
      );
    };

    return {
      xMax,
      temp: {
        series: tempSeries,
        hLines: [{ key: 'target', y: cook.targetC, label: `Target ${fmtTemp(cook.targetC)}` }],
        vLines,
        xRanges,
        tooltipExtra: tempTooltipExtra
      },
      finish: { series: finishSeries, bands: finishBands, hLines: finishLines, vLines }
    };
  }, [detail, versions, modelVersion]);

  const runFit = () => {
    CookService.fit(id, modelVersion || undefined)
      .then(() => { setMessage({ severity: 'success', text: 'Analysis saved to cook_fits.' }); load(); })
      .catch(err => setMessage({ severity: 'error', text: apiErr(err) }));
  };

  if (!detail) {
    return message ? <Alert severity={message.severity}>{message.text}</Alert> : null;
  }

  const { cook, samples, estimates, fits } = detail;

  return (
    <>
      <div className={classes.header}>
        <Button component={Link} to="/cooks" size="small" startIcon={<ArrowBackIcon />}>Cooks</Button>
        <Typography variant="h6">
          {fmtFoodType(cook.foodType)}{cook.cut ? ` — ${cook.cut}` : ''}
        </Typography>
      </div>

      <div className={classes.facts}>
        <Fact label="Started">{new Date(cook.startedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</Fact>
        <Fact label="Status">{STATUS_LABELS[cook.status]}{cook.outcome ? ` · ${cook.outcome}` : ''}</Fact>
        <Fact label="Duration">{fmtDuration(cook.durationS)}</Fact>
        <Fact label="Weight">{fmtWeight(cook.weightG)}</Fact>
        <Fact label="Target">{fmtTemp(cook.targetC)}</Fact>
        <Fact label="Pit setpoint">{fmtTemp(cook.pitSetpointC)}</Fact>
        <Fact label="Removed at core">{fmtTemp(cook.removedCoreC)}</Fact>
        <Fact label="Rested peak">{fmtTemp(cook.restedPeakCoreC)}</Fact>
        <Fact label="Planned meal">
          <PlannedMealLink cookId={cook.id} plannedMeal={detail.plannedMeal} onChange={load} />
        </Fact>
      </div>
      {message && <Alert severity={message.severity} onClose={() => setMessage(null)} style={{ marginBottom: 16 }}>{message.text}</Alert>}

      <CookNotes cookId={cook.id} notes={cook.notes} />

      <Paper className={classes.panel}>
        <div className={classes.panelHeader}>
          <Typography variant="subtitle1">Temperatures</Typography>
          {versions.length > 1 && (
            <FormControl size="small" className={classes.modelSelect}>
              <InputLabel id="cook-model">Stall from</InputLabel>
              <Select labelId="cook-model" value={modelVersion} onChange={e => setModelVersion(e.target.value)}>
                {versions.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </div>
        {samples.length === 0
          ? <Typography variant="body2" className={classes.note}>No samples logged yet.</Typography>
          : <TimeChart height={340} xMax={charts.xMax} formatY={fmtC} yUnit="°C"
              ariaLabel="Probe temperatures against elapsed time" {...charts.temp} />}
        <div className={classes.note}>
          Shaded: where the estimator{modelVersion ? ` (${modelVersion})` : ''} classified a stall.
          Faint lines are the eight raw thermistors, tip to handle.
        </div>
      </Paper>

      <Paper className={classes.panel}>
        <div className={classes.panelHeader}>
          <Typography variant="subtitle1">Predicted vs actual finish</Typography>
        </div>
        {estimates.length === 0
          ? <Typography variant="body2" className={classes.note}>No estimates logged for this cook.</Typography>
          : <TimeChart height={260} xMax={charts.xMax} formatY={fmtElapsed}
              ariaLabel="Predicted finish time against elapsed time" {...charts.finish} />}
        <div className={classes.note}>
          Each point is when the model expected core to reach target, as time since the start.
          A perfect model is a flat line on the actual finish. Shaded: the model's low–high range.
          {detail.markers.reachedTargetS === null && ' Core never reached target, so there is no actual finish.'}
        </div>
      </Paper>

      <Paper className={classes.panel}>
        <div className={classes.panelHeader}>
          <Typography variant="subtitle1">Offline fit</Typography>
          <Button size="small" color="primary" variant="outlined" onClick={runFit}>
            Run analysis{modelVersion ? ` (${modelVersion})` : ''}
          </Button>
        </div>
        {fits.length === 0
          ? <Typography variant="body2" className={classes.note}>Not analysed yet.</Typography>
          : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">τ early</TableCell>
                  <TableCell align="right">τ post-stall</TableCell>
                  <TableCell align="right">Stall</TableCell>
                  <TableCell align="right">Carryover</TableCell>
                  <TableCell align="right">k</TableCell>
                  <TableCell align="right">MAE 60 / 30 / 10 min</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fits.map(f => (
                  <TableRow key={f.id}>
                    <TableCell>{f.modelVersion}</TableCell>
                    <TableCell align="right">{fmtDuration(f.tauEarlyS)}</TableCell>
                    <TableCell align="right">{fmtDuration(f.tauPostStallS)}</TableCell>
                    <TableCell align="right">
                      {f.stallDurationS === null ? '—'
                        : `${fmtDuration(f.stallDurationS)} (${fmtTemp(f.stallStartedC)} → ${fmtTemp(f.stallEndedC)})`}
                    </TableCell>
                    <TableCell align="right">{fmtTemp(f.observedCarryoverC)}</TableCell>
                    <TableCell align="right">{f.carryoverK === null ? '—' : f.carryoverK.toFixed(3)}</TableCell>
                    <TableCell align="right">
                      {[f.mae60minS, f.mae30minS, f.mae10minS]
                        .map(v => (v === null ? '—' : `${Math.round(v / 60)}m`)).join(' / ')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </Paper>
    </>
  );
};

export default CookDetailPage;
